import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { expenseService } from "@/api/expense.service";
import type { OfxImportResult, ParsedOfxTransaction } from "@/api/types";
import { CATEGORIES } from "@/api/categories";

const fmt = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

interface ImportRow extends ParsedOfxTransaction {
  selected: boolean;
  category: string;
  sharedWith: boolean;
}

interface OfxImportDialogProps {
  open: boolean;
  transactions: ParsedOfxTransaction[];
  onClose: () => void;
  onImported: (result: OfxImportResult) => void;
}

export default function OfxImportDialog({ open, transactions, onClose, onImported }: OfxImportDialogProps) {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setRows(transactions.map(t => ({
      ...t,
      selected: !t.alreadyImported,
      category: t.suggestedCategory,
      sharedWith: false,
    })));
    setError("");
  }, [transactions]);

  const selectedCount = rows.filter(r => r.selected).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const updateRow = (index: number, patch: Partial<ImportRow>) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleToggleAll = () => {
    const next = !allSelected;
    setRows(prev => prev.map(r => ({ ...r, selected: next })));
  };

  const handleImport = async () => {
    setLoading(true);
    setError("");
    try {
      const items = rows.filter(r => r.selected).map(r => ({
        fitid: r.fitid, label: r.label, amount: r.amount, date: r.date, category: r.category, sharedWith: r.sharedWith,
      }));
      const result = await expenseService.confirmOfxImport(items);
      onImported(result);
    } catch (e: any) {
      setError(e.response?.data?.error || "Erreur lors de l'import.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Importer un relevé OFX</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {rows.length === 0 ? (
          <Typography color="text.secondary">Aucune opération à importer trouvée dans ce fichier.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleToggleAll} />
                  </TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Libellé</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell>Catégorie</TableCell>
                  <TableCell align="center">Partagé</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row.fitid} hover selected={row.selected}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={row.selected} onChange={e => updateRow(i, { selected: e.target.checked })} />
                    </TableCell>
                    <TableCell>{new Date(row.date).toLocaleDateString("fr-FR")}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        fullWidth
                        value={row.label}
                        onChange={e => updateRow(i, { label: e.target.value })}
                      />
                      {row.alreadyImported && <Chip label="Déjà importé" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />}
                    </TableCell>
                    <TableCell align="right">{fmt(row.amount)}</TableCell>
                    <TableCell>
                      <FormControl variant="standard" size="small" fullWidth>
                        <Select value={row.category} onChange={e => updateRow(i, { category: e.target.value })}>
                          {CATEGORIES.map(c => (
                            <MenuItem key={c.value} value={c.value}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c.color, flexShrink: 0 }} />
                                {c.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={row.sharedWith} onChange={e => updateRow(i, { sharedWith: e.target.checked })} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={loading || selectedCount === 0} onClick={handleImport}>
          {loading ? <CircularProgress size={20} /> : `Importer (${selectedCount} sélectionnée${selectedCount > 1 ? "s" : ""})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
